import React, { Component, useEffect, useRef, useState } from "react";
import FacebookIcon from "@mui/icons-material/Facebook";
import { Box, Button, CircularProgress, Divider, Typography } from "@mui/material";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import ApiService from "../services/ApiService";

const GRAPH_VERSION = "v19.0";
const FACEBOOK_SCOPE = "public_profile,email";
/** Silent hang fallback after FB.login was invoked (popup may stay open a long time). */
const FACEBOOK_LOGIN_HANG_TIMEOUT_MS = 45000;
const FACEBOOK_LOGIN_DIDNT_OPEN_MESSAGE = "Facebook sign-in didn't open. Please try again.";

function loadFacebookSdk() {
  if (window.FB) return Promise.resolve(window.FB);
  if (window.__fbSdkPromise) return window.__fbSdkPromise;
  window.__fbSdkPromise = new Promise((resolve, reject) => {
    const previous = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      if (typeof previous === "function") previous();
      resolve(window.FB);
    };
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => reject(new Error("Facebook SDK failed to load"));
    document.body.appendChild(script);
  });
  return window.__fbSdkPromise;
}

async function resolveClientId(fetcher) {
  if (typeof fetcher !== "function") return "";
  try {
    const id = await fetcher();
    return typeof id === "string" ? id.trim() : "";
  } catch {
    return "";
  }
}

function OAuthProviderButtonsInner({ onAuthenticated, onError }) {
  const [googleClientId, setGoogleClientId] = useState(null);
  const [facebookAppId, setFacebookAppId] = useState(null);
  const [facebookReady, setFacebookReady] = useState(false);
  const [facebookBusy, setFacebookBusy] = useState(false);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(320);
  const facebookTimeoutRef = useRef(null);
  const facebookLoginActiveRef = useRef(false);
  const googleWrapRef = useRef(null);

  function clearFacebookTimeout() {
    if (facebookTimeoutRef.current) {
      clearTimeout(facebookTimeoutRef.current);
      facebookTimeoutRef.current = null;
    }
  }

  function resetFacebookBusy() {
    facebookLoginActiveRef.current = false;
    clearFacebookTimeout();
    setFacebookBusy(false);
  }

  useEffect(() => () => clearFacebookTimeout(), []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      resolveClientId(ApiService.googleClientId?.bind(ApiService)),
      resolveClientId(ApiService.facebookAppId?.bind(ApiService)),
    ]).then(([googleId, facebookId]) => {
      if (cancelled) return;
      setGoogleClientId(googleId);
      setFacebookAppId(facebookId);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const el = googleWrapRef.current;
    if (!el) return undefined;
    const updateWidth = () => {
      const next = Math.floor(el.clientWidth);
      if (next > 0) setGoogleBtnWidth(Math.min(next, 400));
    };
    updateWidth();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, [googleClientId]);

  useEffect(() => {
    if (!facebookAppId) {
      setFacebookReady(false);
      return undefined;
    }
    let cancelled = false;
    setFacebookReady(false);
    loadFacebookSdk()
      .then((FB) => {
        if (cancelled || !FB?.login) return;
        // fedCM: false forces the classic OAuth popup. When Chrome has FedCM
        // disabled for the site (after a dismissal), FedCM-mode FB.login never
        // calls back and the button hangs — see ORK-173 / Meta FedCM docs.
        FB.init({
          appId: facebookAppId,
          cookie: false,
          xfbml: false,
          version: GRAPH_VERSION,
          fedCM: false,
        });
        if (!cancelled) setFacebookReady(true);
      })
      .catch(() => {
        if (!cancelled) setFacebookReady(false);
      });
    return () => { cancelled = true; };
  }, [facebookAppId]);

  async function onGoogleSuccess(response) {
    try {
      if (typeof ApiService.signInWithGoogle !== "function") {
        throw new Error("Google sign-in is not available");
      }
      await ApiService.signInWithGoogle(response.credential);
      if (onAuthenticated) onAuthenticated();
    } catch (error) {
      if (onError) onError(error?.response?.data?.message || error?.message || "Google sign-in failed");
    }
  }

  function startFacebookLogin() {
    if (!facebookAppId) {
      if (onError) onError("Facebook sign-in is not set up yet.");
      return;
    }
    if (!facebookReady || !window.FB?.login) {
      if (onError) onError("Facebook is still loading. Try again.");
      return;
    }
    if (typeof ApiService.signInWithFacebook !== "function") {
      if (onError) onError("Facebook sign-in is not available");
      return;
    }

    facebookLoginActiveRef.current = true;
    setFacebookBusy(true);
    clearFacebookTimeout();

    try {
      // Must stay synchronous in the click handler — any await before this loses the user gesture
      // and Chrome blocks the Facebook popup.
      window.FB.login((loginResponse) => {
        const accessToken = loginResponse?.authResponse?.accessToken;
        // Closing the popup (or declining) → cancelled. Do not leave a prior
        // "didn't open" toast (ORK-173 Retest 5/6).
        if (!accessToken) {
          resetFacebookBusy();
          if (onError) onError("Facebook sign-in was cancelled.");
          return;
        }
        // Late success after hang timeout: still complete sign-in.
        facebookLoginActiveRef.current = false;
        clearFacebookTimeout();
        setFacebookBusy(true);
        ApiService.signInWithFacebook(accessToken)
          .then(() => { if (onAuthenticated) onAuthenticated(); })
          .catch((error) => {
            if (onError) onError(error?.response?.data?.message || error?.message || "Facebook sign-in failed");
          })
          .finally(() => resetFacebookBusy());
      }, { scope: FACEBOOK_SCOPE });

      // FB.login started — popup may stay open while the user signs in.
      // Do not show "didn't open" while waiting; only silently unlock after a
      // long hang so the user can retry (ORK-173 Retest 6).
      facebookTimeoutRef.current = setTimeout(() => {
        facebookLoginActiveRef.current = false;
        setFacebookBusy(false);
        facebookTimeoutRef.current = null;
      }, FACEBOOK_LOGIN_HANG_TIMEOUT_MS);
    } catch (error) {
      // FB.login never started — this is the only "didn't open" path.
      resetFacebookBusy();
      if (onError) onError(FACEBOOK_LOGIN_DIDNT_OPEN_MESSAGE);
    }
  }

  // Still loading IDs
  if (googleClientId === null && facebookAppId === null) return null;
  if (!googleClientId && !facebookAppId) return null;

  const facebookDisabled = !facebookReady || facebookBusy;
  const facebookLabel = facebookBusy
    ? "Connecting…"
    : (!facebookReady ? "Loading Facebook…" : "Continue with Facebook");

  return (
    <Box sx={{ mt: 2, width: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <Divider sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: "#94A3B8", px: 1 }}>Or continue with</Typography>
      </Divider>
      {googleClientId ? (
        <GoogleOAuthProvider clientId={googleClientId}>
          <Box
            ref={googleWrapRef}
            sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", display: "flex", justifyContent: "center", mb: 1.5, overflow: "hidden" }}
          >
            <GoogleLogin
              text="continue_with"
              onSuccess={onGoogleSuccess}
              onError={() => { if (onError) onError("Google sign-in failed"); }}
              width={String(googleBtnWidth)}
              size="large"
              shape="rectangular"
              logo_alignment="center"
            />
          </Box>
        </GoogleOAuthProvider>
      ) : null}
      {facebookAppId ? (
        <Button
          fullWidth
          variant="outlined"
          onClick={startFacebookLogin}
          disabled={facebookDisabled}
          startIcon={facebookBusy || !facebookReady
            ? <CircularProgress size={16} />
            : <FacebookIcon sx={{ color: "#1877F2" }} />}
          sx={{ py: 1.125 }}
        >
          {facebookLabel}
        </Button>
      ) : null}
    </Box>
  );
}

class OAuthButtonsErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error("OAuthProviderButtons failed:", error);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function OAuthProviderButtons(props) {
  return (
    <OAuthButtonsErrorBoundary>
      <OAuthProviderButtonsInner {...props} />
    </OAuthButtonsErrorBoundary>
  );
}

export default OAuthProviderButtons;
export { OAuthProviderButtonsInner, resolveClientId };
